import React from 'react';

import { Link } from '~/lib/react-router-dom';

import { Stack, Typography } from '@mui/material';

import { CreateSinglePromoClonePayload } from '~/hooks/usePromo';

import { BulkCreatePromosResult, PromoMeta } from '~/services/PromoService';
import { formatDateLongMonthWithMeridiemNoSeconds } from '~/services/TimeService';

import PageSubheader from '../Common/Elements/PageSubheader';
import UserName from '../Common/UserName';
import DebugModal from '../DebugModal/DebugModal';

import PromoCopyButton from './PromoCopyButton';

type PromoSummaryActionsProps = {
  promoCode: App.PromoData;
  parentPromoCode: App.PromoData | null;
  promoMeta: PromoMeta | null;
  createSinglePromoClone?: (payload: CreateSinglePromoClonePayload) => Promise<BulkCreatePromosResult>;
};

const PromoSummaryActions = ({
  promoCode,
  parentPromoCode,
  promoMeta,
  createSinglePromoClone,
}: PromoSummaryActionsProps) => {
  return (
    <PageSubheader title="Promo code details">
      {promoCode && promoCode.cloned_from_promo_id && (
        <Link target="_blank" to={`/promos/${promoCode.cloned_from_promo_id}`}>
          {`[child of ${parentPromoCode?.code_name ?? promoCode.cloned_from_promo_id}]`}
        </Link>
      )}
      {promoCode?.email_addresses &&
        promoCode?.email_addresses.length > 0 &&
        promoCode?.email_addresses?.join(',').length < 200 && (
          <Typography variant="subtitle2">[for {promoCode.email_addresses.join(', ')}]</Typography>
        )}
      <Stack direction="row" gap={1}>
        {promoCode && promoCode.added_by && <UserName asLink userId={promoCode.added_by} />}
        {promoCode && promoCode.created_at && <Typography variant="subtitle2">@</Typography>}
        {promoCode && promoCode.created_at && (
          <Typography variant="subtitle2" title="(created at)">
            {promoCode.created_at}
          </Typography>
        )}
      </Stack>
      {promoCode && promoCode.updated_at && promoCode.updated_at != promoCode.created_at && (
        <Typography variant="subtitle2" title="(updated at)">
          {promoCode.updated_at} by <UserName asLink userId={promoCode.added_by} />
        </Typography>
      )}

      <Stack direction="row">
        {promoCode?.updated_at &&
          formatDateLongMonthWithMeridiemNoSeconds(promoCode?.updated_at) !=
            formatDateLongMonthWithMeridiemNoSeconds(promoCode?.created_at) && (
            <Typography variant="subtitle2" title="(updated at)">
              {formatDateLongMonthWithMeridiemNoSeconds(promoCode.updated_at)} by{' '}
              <UserName asLink userId={promoCode?.added_by} />
            </Typography>
          )}
        {promoCode && (
          <DebugModal
            type="generic"
            title="Raw Promo Data"
            data={JSON.parse(
              JSON.stringify({
                promoCode: promoCode,
                parentPromoCode: parentPromoCode,
                promoMeta: promoCode.meta,
                promoDiscounts: promoCode.promo_discounts,
              }),
            )}
          />
        )}
        {parentPromoCode && (
          <DebugModal type="generic" title="Raw Parent Promo Data" data={JSON.parse(JSON.stringify(parentPromoCode))} />
        )}
        {promoCode && createSinglePromoClone && (
          <PromoCopyButton
            isLoading={false}
            promoId={promoCode?.id_promo_code}
            codeName={promoCode?.code_name}
            parentCodeName={parentPromoCode?.code_name}
            createSinglePromoClone={createSinglePromoClone}
          />
        )}
      </Stack>
    </PageSubheader>
  );
};

export default PromoSummaryActions;
